/** AchievementStore — プレイヤーの実績バッジを永続化。
 *  Reference: docs/CLAUDE.md §3.3 Skill Honored(純粋称号、課金不可)
 *
 *  Callers:
 *    - web/src/main.ts: onStageClear 等のイベントで recordEvent() を呼ぶ
 *    - web/src/ui/screens/HomeScreen.ts: getAll() / getUnlocked() で UI 表示
 */

export type AchievementId =
  | 'first_clear'        // 初クリア
  | 'clear_10'           // 10 ステージクリア
  | 'clear_25'           // 25 ステージクリア
  | 'clear_chapter_1'    // 章 1 完走(40 ステージ)
  | 'coverage_90'        // 占領率 90% 以上で勝利
  | 'no_helper_3'        // ヘルパー無しで 3 連勝
  | 'no_booster_clear'   // ブースター無しでクリア
  | 'mask_first'         // 構図マスク勝利初達成
  | 'mask_all_3'         // マスク勝利 3 種(2-15/16/17)全達成
  | 'combo_3'            // コンボ 3 達成
  | 'combo_5'            // コンボ 5 達成
  | 'rival_take_100'     // ライバル領地 累計 100 マス奪取
  | 'streak_7'           // 7 日連続ログイン
  | 'collect_5_characters' // キャラクター 5 種解放
  | 'play_15min_total';  // 累計プレイ時間 15 分(モチベ初期離脱前の節目)

export interface AchievementMeta {
  readonly id: AchievementId;
  readonly title: string;
  readonly description: string;
  readonly icon: string; // 絵文字
}

export const ACHIEVEMENT_META: Record<AchievementId, AchievementMeta> = {
  first_clear:           { id: 'first_clear',           title: '初めての一筆',     description: '初めてのステージをクリア',         icon: '🎨' },
  clear_10:              { id: 'clear_10',              title: '見習い絵師',       description: '10 ステージクリア',               icon: '🖌️' },
  clear_25:              { id: 'clear_25',              title: '熟練の手',         description: '25 ステージクリア',               icon: '🏆' },
  clear_chapter_1:       { id: 'clear_chapter_1',       title: 'ルネサンス完走',   description: '章 1 の全 40 ステージを制覇',    icon: '👑' },
  coverage_90:           { id: 'coverage_90',           title: '完璧主義',         description: '占領率 90% 以上で勝利',          icon: '✨' },
  no_helper_3:           { id: 'no_helper_3',           title: '純粋なる筆',       description: 'ヘルパー無しで 3 連勝',          icon: '🎯' },
  no_booster_clear:      { id: 'no_booster_clear',      title: '素手の腕前',       description: 'ブースター無しでクリア',          icon: '💪' },
  mask_first:            { id: 'mask_first',            title: '名画の輪郭',       description: '初めて構図マスクを完成',          icon: '🖼️' },
  mask_all_3:            { id: 'mask_all_3',            title: '美術館の主',       description: 'ハート / 星 / 花 全てを完成',   icon: '🏛️' },
  combo_3:               { id: 'combo_3',               title: '連筆三色',         description: 'コンボ ×3 達成',               icon: '🔥' },
  combo_5:               { id: 'combo_5',               title: '電光石火',         description: 'コンボ ×5 達成',               icon: '⚡' },
  rival_take_100:        { id: 'rival_take_100',        title: '征服者',           description: 'ライバル領地を累計 100 マス奪取', icon: '🗡️' },
  streak_7:              { id: 'streak_7',              title: '日々の制作',       description: '7 日連続ログイン',               icon: '📅' },
  collect_5_characters:  { id: 'collect_5_characters',  title: '絵筆のコレクター', description: 'キャラクター 5 種を解放',        icon: '🎭' },
  play_15min_total:      { id: 'play_15min_total',      title: 'アトリエに座る',   description: '累計プレイ時間 15 分',           icon: '🪑' },
};

export const ACHIEVEMENT_IDS: readonly AchievementId[] = Object.keys(ACHIEVEMENT_META) as AchievementId[];

export interface AchievementProgress {
  /** 累計クリア数(stage clear イベント毎にインクリメント) */
  totalClears: number;
  /** 累計連勝数(ヘルパー無し時のみカウント、ヘルパー使用 or 失敗でリセット) */
  noHelperWinStreak: number;
  /** 累計ライバル領地奪取マス数 */
  rivalCellsTaken: number;
  /** 累計プレイ時間(秒) */
  totalPlaySec: number;
  /** ベストコンボ数 */
  bestCombo: number;
  /** 解放済 achievement の id 集合 */
  unlocked: AchievementId[];
}

const STORAGE_KEY = 'ck.achievements';

const DEFAULT_PROGRESS: AchievementProgress = {
  totalClears: 0,
  noHelperWinStreak: 0,
  rivalCellsTaken: 0,
  totalPlaySec: 0,
  bestCombo: 0,
  unlocked: [],
};

export class AchievementStore {
  private state: AchievementProgress;

  constructor() {
    this.state = this.load();
  }

  private load(): AchievementProgress {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return { ...DEFAULT_PROGRESS, unlocked: [] };
      const parsed = JSON.parse(raw) as Partial<AchievementProgress>;
      return {
        totalClears:       parsed.totalClears       ?? 0,
        noHelperWinStreak: parsed.noHelperWinStreak ?? 0,
        rivalCellsTaken:   parsed.rivalCellsTaken   ?? 0,
        totalPlaySec:      parsed.totalPlaySec      ?? 0,
        bestCombo:         parsed.bestCombo         ?? 0,
        unlocked: Array.isArray(parsed.unlocked)
          ? parsed.unlocked.filter((a): a is AchievementId => ACHIEVEMENT_IDS.includes(a as AchievementId))
          : [],
      };
    } catch {
      return { ...DEFAULT_PROGRESS, unlocked: [] };
    }
  }

  private save(): void {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(this.state));
    } catch {
      /* noop(プライベートモード等) */
    }
  }

  /** イベント発火:state を更新し、条件を満たした achievement を解放する。新規解放 id のリストを返す。 */
  recordStageCleared(opts: {
    stageId: string;
    coverage: number;
    usedBooster: boolean;
    usedHelper: boolean;
    clearedStagesCount: number;
    isMaskStage: boolean;
    maskStagesCleared: number; // ハート/星/花 のうち何個クリアしたか
    charactersUnlockedCount: number;
  }): AchievementId[] {
    this.state.totalClears = opts.clearedStagesCount;
    // ヘルパー無し連勝
    if (opts.usedHelper) {
      this.state.noHelperWinStreak = 0;
    } else {
      this.state.noHelperWinStreak += 1;
    }
    const newlyUnlocked: AchievementId[] = [];
    const tryUnlock = (id: AchievementId, cond: boolean): void => {
      if (cond && !this.state.unlocked.includes(id)) {
        this.state.unlocked.push(id);
        newlyUnlocked.push(id);
      }
    };
    tryUnlock('first_clear',          this.state.totalClears >= 1);
    tryUnlock('clear_10',             this.state.totalClears >= 10);
    tryUnlock('clear_25',             this.state.totalClears >= 25);
    tryUnlock('clear_chapter_1',      this.state.totalClears >= 40);
    tryUnlock('coverage_90',          opts.coverage >= 0.9);
    tryUnlock('no_helper_3',          this.state.noHelperWinStreak >= 3);
    tryUnlock('no_booster_clear',     !opts.usedBooster);
    tryUnlock('mask_first',           opts.isMaskStage);
    tryUnlock('mask_all_3',           opts.maskStagesCleared >= 3);
    tryUnlock('collect_5_characters', opts.charactersUnlockedCount >= 5);
    this.save();
    return newlyUnlocked;
  }

  recordCombo(combo: number): AchievementId[] {
    if (combo <= this.state.bestCombo) return [];
    this.state.bestCombo = combo;
    const newly: AchievementId[] = [];
    const tryUnlock = (id: AchievementId, cond: boolean): void => {
      if (cond && !this.state.unlocked.includes(id)) {
        this.state.unlocked.push(id);
        newly.push(id);
      }
    };
    tryUnlock('combo_3', combo >= 3);
    tryUnlock('combo_5', combo >= 5);
    this.save();
    return newly;
  }

  recordRivalCellsTaken(count: number): AchievementId[] {
    this.state.rivalCellsTaken += count;
    if (!this.state.unlocked.includes('rival_take_100') && this.state.rivalCellsTaken >= 100) {
      this.state.unlocked.push('rival_take_100');
      this.save();
      return ['rival_take_100'];
    }
    this.save();
    return [];
  }

  recordStreakDay(currentStreakDay: number): AchievementId[] {
    if (!this.state.unlocked.includes('streak_7') && currentStreakDay >= 7) {
      this.state.unlocked.push('streak_7');
      this.save();
      return ['streak_7'];
    }
    return [];
  }

  recordPlaytime(deltaSec: number): AchievementId[] {
    this.state.totalPlaySec += deltaSec;
    if (!this.state.unlocked.includes('play_15min_total') && this.state.totalPlaySec >= 900) {
      this.state.unlocked.push('play_15min_total');
      this.save();
      return ['play_15min_total'];
    }
    this.save(); // playtime は累積で頻繁に save される、最適化したいなら debounce
    return [];
  }

  getAll(): readonly AchievementMeta[] {
    return ACHIEVEMENT_IDS.map((id) => ACHIEVEMENT_META[id]);
  }

  getUnlocked(): readonly AchievementId[] {
    return [...this.state.unlocked];
  }

  isUnlocked(id: AchievementId): boolean {
    return this.state.unlocked.includes(id);
  }

  getProgress(): Readonly<AchievementProgress> {
    return this.state;
  }
}
